import { useMemo } from 'react'
import { Trans } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ConditionNumericOperator,
  ConditionTextOperator,
  DATE_OPERATOR_WITH_RANGE_RHS,
  DATE_OPERATOR_WITH_RHS,
  FunctionIfProps,
  NUMERIC_OPERATOR_WITH_INPUT_RHS,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
  OPERATOR_WITH_BETWEEN_RHS,
  OPERATOR_WITH_INPUT_RHS,
  TableAdapter,
} from '@makyoapp/makyo-common'

import { getOperatorDefaultOption, validateRhsEndValues } from '@/shared/condition'

import { ColumnOrCustomDd } from './ColumnOrCustomDd'
import { FunctionConditionOperatorSelect } from './FunctionConditionOperatorSelect'
import { FunctionConditionSelect } from './FunctionConditionSelect'

export function FunctionIf({
  id,
  value,
  onChange,
  colOptions,
  table,
  tables,
}: FunctionIfProps & { tables: TableAdapter[] }) {
  //
  const theValue = useMemo(() => {
    const newValue = {
      condition: {
        id: '',
        lhs: '',
        operator: ConditionTextOperator.IsEmpty,
        rhs: {
          isCustom: true,
          value: '',
        },
        rhsEnd: undefined,
        caseSensitive: false,
      },
    }
    if (value) {
      return {
        condition: {
          id: '',
          lhs: value.condition?.lhs || newValue.condition.lhs,
          operator: value.condition?.operator || newValue.condition.operator,
          rhs: value.condition?.rhs || newValue.condition.rhs,
          rhsEnd: value.condition?.rhsEnd,
          caseSensitive: false,
        },
      }
    }
    return newValue
  }, [value])

  const cond = theValue.condition
  const colObj = useMemo(() => {
    return colOptions.find((c) => c.id === cond?.lhs)
  }, [colOptions, cond?.lhs])

  const varColTypes = useMemo(() => {
    if (colObj) {
      if (
        colObj.type === ColumnTypes.Text &&
        [ConditionTextOperator.IsNotContainedIn, ConditionTextOperator.IsContainedIn].includes(
          cond?.operator as ConditionTextOperator
        )
      ) {
        return [ColumnTypes.Text, ColumnTypes.Array]
      } else if ([ColumnTypes.Rating, ColumnTypes.Number].includes(colObj.type)) {
        return [ColumnTypes.Rating, ColumnTypes.Number]
      } else {
        return [colObj.type]
      }
    } else {
      return []
    }
  }, [colObj, cond])

  const isDateSingleOperator = useMemo(() => {
    return DATE_OPERATOR_WITH_RHS.includes(cond.operator)
  }, [cond.operator])

  const isDateRangeOperator = useMemo(() => {
    return DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)
  }, [cond.operator])

  const isDateOperator = isDateSingleOperator || isDateRangeOperator

  return (
    <div id={id} className='flex flex-col gap-4 w-full'>
      <div className='flex gap-2 flex-col w-full'>
        <div className='flex-c p-2 border bdr rounded-md bg-zinc-50 dark:bg-slate-700'>
          <div className='text-m-sm shrink-0 w-[36px]'>IF</div>
          <div className='flex flex-col gap-2 min-w-0 w-full'>
            <div className='flex-c w-full'>
              <div className='w-full min-w-0'>
                <FunctionConditionSelect
                  toggleClassName='dark:hover:bg-slate-800!'
                  id={`${id}-lhs`}
                  value={cond.lhs}
                  table={table}
                  onChange={(lhs) => {
                    const isSame = cond.lhs === lhs
                    const newColObj = colOptions.find((c) => c.id === lhs)
                    let nextOp = cond.operator

                    let extra: Record<string, unknown> = {}

                    if (!isSame) {
                      if (cond.operator) {
                        if (newColObj) {
                          nextOp = getOperatorDefaultOption(newColObj, table, tables)
                        }
                      }

                      extra = validateRhsEndValues(nextOp)
                    }

                    onChange({
                      ...theValue,
                      condition: {
                        ...theValue.condition,
                        lhs: lhs.toString(),
                        operator: nextOp,
                        ...extra,
                      },
                    })
                  }}
                  colOptions={colOptions}
                />
              </div>
              <FunctionConditionOperatorSelect
                id={id ? `${id}-op` : undefined}
                selected={cond.operator}
                table={table}
                colOptions={colOptions}
                value={theValue.condition}
                onChange={(opt) => {
                  const extra = validateRhsEndValues(opt)

                  onChange({
                    ...theValue,
                    condition: {
                      ...theValue.condition,
                      operator: opt,
                      ...extra,
                    },
                  })
                }}
              />
            </div>
            {OPERATOR_WITH_BETWEEN_RHS.includes(cond.operator) && (
              <div className='uppercase text-m-ss'>
                <Trans>Lower Bound</Trans>
              </div>
            )}
            {(OPERATOR_WITH_INPUT_RHS.includes(cond.operator) ||
              DATE_OPERATOR_WITH_RHS.includes(cond.operator) ||
              NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(cond.operator as ConditionNumericOperator) ||
              DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)) && (
              <ColumnOrCustomDd
                withCustomValue
                inputClassName='dark:hover:bg-slate-800!'
                inputFocusClassName='dark:bg-slate-800!'
                withExtraOptions={isDateSingleOperator ? ['date'] : undefined}
                id={`${id}-rhs`}
                table={table}
                value={
                  cond?.rhs || {
                    isCustom: true,
                    value: '',
                  }
                }
                customValueType={
                  isDateOperator ? 'date' : NUMERIC_OPERATOR_WITH_INPUT_RHS.includes(cond.operator) ? 'number' : 'text'
                }
                toggleClassName='dark:hover:bg-slate-800!'
                onChange={(v) => {
                  onChange({
                    ...value,
                    condition: {
                      ...theValue.condition,
                      rhs: v,
                    },
                  })
                }}
                excludeColumns={cond.lhs ? [cond.lhs] : []}
              />
            )}
            {OPERATOR_WITH_BETWEEN_RHS.includes(cond.operator) && (
              <div className='uppercase text-m-ss'>
                <Trans>Upper Bound</Trans>
              </div>
            )}
            {(NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(cond.operator as ConditionNumericOperator) ||
              DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)) && (
              <ColumnOrCustomDd
                inputClassName='dark:hover:bg-slate-800!'
                inputFocusClassName='dark:bg-slate-800!'
                withCustomValue
                withExtraOptions={isDateSingleOperator ? ['date'] : undefined}
                id={`${id}-rhs-end`}
                table={table}
                customValueType={
                  isDateOperator ? 'date' : NUMERIC_OPERATOR_WITH_INPUT_RHS.includes(cond.operator) ? 'number' : 'text'
                }
                value={
                  cond?.rhsEnd || {
                    isCustom: true,
                    value: '',
                  }
                }
                onChange={(v) => {
                  onChange({
                    ...theValue,
                    condition: {
                      ...theValue.condition,
                      rhsEnd: v,
                    },
                  })
                }}
                excludeColumns={cond.lhs ? [cond.lhs] : []}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
